import styled from '@emotion/styled/macro';
import { css } from '@emotion/react';
import PropTypes from 'prop-types';
import { A } from '../../base/links';
import customIcons from '../../styles/icons';
import { colors, fonts, leading } from '../../styles/theme';

const pageBoxSize = '28px';

const PaginationWrapper = styled('nav')`
  width: 100%;
  clear: both;
  position: relative;
  text-align: center;
  display: flex;
  margin: ${leading.full} 0;
  justify-content: center;
  align-items: center;
`;

const currentPageStyle = css`
  font-weight: ${fonts.sans.weight.bold};
  color: ${colors.white};
  border-radius: 50%;
  background: ${colors.blue.primaryLight};
  border: 1px solid ${colors.blue.primaryLight};
  min-width: ${pageBoxSize};
  padding: 0 5px;
  height: ${pageBoxSize};
  line-height: ${pageBoxSize};
  justify-content: center;
  &:hover {
    text-decoration: none;
    cursor: default;
  }
`;

const Page = styled(A)`
  display: inline-flex;
  margin: 0 7px;
  font-size: ${fonts.sans.size.large};
  height: ${pageBoxSize};
  line-height: ${pageBoxSize};
  ${({ current }) => (current ? currentPageStyle : '')};
`;

const disabledStyle = css`
  color: ${colors.grey.secondaryDark};
  pointer-events: none;
  &:before,
  &:after {
    color: ${colors.grey.secondaryDark};
  }
  &:hover {
    text-decoration: none;
    cursor: default;
  }
`;

const NavigateIcon = styled(A)`
  display: inline-block;
  font-size: ${fonts.sans.size.small};
  line-height: ${leading.full};
  text-transform: uppercase;
  &:before,
  &:after {
    ${fonts.icons};
    color: inherit;
    font-size: 16px;
    line-height: ${leading.full};
    color: ${colors.blue.primary};
    margin-top: -1px;
  }
  ${({ disabled }) => (disabled ? disabledStyle : '')};
`;

const Prev = styled(NavigateIcon)`
  margin-right: 15px;
  &:before {
    content: '←';
    margin-right: 3px;
    float: left;
  }
`;

const Next = styled(NavigateIcon)`
  margin-left: 15px;
  &:after {
    content: '→';
    margin-left: 3px;
    float: right;
  }
`;

const First = styled(Prev)`
  margin-right: 25px;
  &:before {
    content: '|${customIcons['angle-left']}';
  }
`;

const Last = styled(Next)`
  margin-left: 25px;
  &:after {
    content: '${customIcons['angle-right']}|';
  }
`;

const Pagination = (props) => {
  const { handlePageChange, currentPage, numPages } = props;

  const safeCurrentPage = currentPage > numPages ? numPages : currentPage;

  const prevPage = safeCurrentPage === 1 ? 1 : safeCurrentPage - 1;
  const nextPage = safeCurrentPage === numPages ? numPages : safeCurrentPage + 1;

  const innerPageLinksStart = numPages <= 5 ? 1 : Math.max(1, safeCurrentPage - 2);
  const innerPageLinksEnd = numPages <= 5 ? numPages : Math.min(numPages, safeCurrentPage + 2);

  const range = (start, end) => new Array(end - start + 1).fill(undefined).map((_, i) => i + start);

  const pageLinksByRange = (start, end) =>
    range(start, end).map((page) => (
      <Page
        key={page}
        current={page === safeCurrentPage}
        data-page={page}
        onClick={handlePageChange}
      >
        {page}
      </Page>
    ));

  return (
    <PaginationWrapper>
      {numPages > 5 && <First data-page={1} onClick={handlePageChange} />}
      <Prev data-page={prevPage} onClick={handlePageChange}>
        Prev
      </Prev>
      {pageLinksByRange(innerPageLinksStart, innerPageLinksEnd)}
      <Next data-page={nextPage} onClick={handlePageChange}>
        Next
      </Next>
      {numPages > 5 && <Last data-page={numPages} onClick={handlePageChange} />}
    </PaginationWrapper>
  );
};

Pagination.propTypes = {
  handlePageChange: PropTypes.func.isRequired,
  currentPage: PropTypes.number.isRequired,
  numPages: PropTypes.number.isRequired,
};

export { Pagination };
